import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Video, FileText, Headphones, CheckCircle, Clock, Award, ChevronRight, Lock, ArrowLeft, Download, ExternalLink } from 'lucide-react';
import GoogleSheetsSubmit from '../components/courses/GoogleSheetsSubmit';
import DownloadHandout from '../components/courses/DownloadHandout';
import DocumentViewer from '../components/courses/DocumentViewer';
import { saveAssessmentResponse, saveProgress, fetchUserProgress, updateCurrentStep, redirectToCurrentStep } from '../utils/progressUtils';
import { saveProgress as saveSimplifiedProgress, loadProgress } from '../utils/courseProgress';
// Course content types
type ContentType = 'text' | 'video' | 'podcast' | 'assessment';
// Module interface
interface CourseModule {
  id: string;
  title: string;
  type: ContentType;
  completed: boolean;
  locked: boolean;
  duration: string;
  content?: React.ReactNode;
}
const CourseDetail: React.FC = () => {
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const navigate = useNavigate();
  const {
    user,
    isAuthenticated
  } = useAuth();
  // Course completion states
  const [preAssessmentCompleted, setPreAssessmentCompleted] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [certificateName, setCertificateName] = useState('');
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [certificateGenerated, setCertificateGenerated] = useState(false);
  // Add a state to track if we should show the name input form
  const [showCertificateNameForm, setShowCertificateNameForm] = useState(false);
  // Course data
  const courseTitle = 'From Teacher to Corporate Trainer and Educator';
  const courseSubtitle = 'Untapped Career Paths for Education Students';
  // Course modules
  const [modules, setModules] = useState<CourseModule[]>([{
    id: 'pre-assessment',
    title: 'Pre-Assessment Quiz',
    type: 'assessment',
    completed: false,
    locked: false,
    duration: '15 min',
    content: <PreAssessmentContent onComplete={() => completeModule(0)} data-id="element-595" />
  }, {
    id: 'introduction',
    title: 'Course Introduction',
    type: 'text',
    completed: false,
    locked: true,
    duration: '10 min',
    content: <TextContent title="Welcome to Your New Career Journey" text="This comprehensive course will guide you through the exciting transition from traditional education roles to corporate training and development. As an education professional, you've already developed a powerful set of skills that are highly valued in the corporate world. In this course, we'll show you how to leverage these skills to build a rewarding career as a corporate trainer and educator..." handoutUrl="https://docs.google.com/document/d/1gzSKbeiUnSEr6i_XkLy5qC4qwrVCICWHkV67ZZ4kFQg/edit?usp=sharing" handoutTitle="Course Introduction - Complete Handout" onComplete={() => completeModule(1)} data-id="element-596" />
  }, {
    id: 'video-1',
    title: 'Teacher to Trainer',
    type: 'video',
    completed: false,
    locked: true,
    duration: '18 min',
    content: <VideoContent title="Teacher to Trainer" videoUrl="https://drive.google.com/file/d/1Dv9mLeOakkkj8pYxsRdKXguiADnAxxkV/view?usp=drive_link" onComplete={() => completeModule(2)} data-id="element-597" />
  }, {
    id: 'text-1',
    title: 'Transferable Skills for Corporate Training',
    type: 'text',
    completed: false,
    locked: true,
    duration: '15 min',
    content: <TextContent title="Transferable Skills for Corporate Training" text="Your experience in education has equipped you with numerous skills that transfer seamlessly to corporate training roles. These include instructional design, presentation skills, needs assessment, curriculum development, and more. In this module, we'll explore how these skills are applied in corporate settings..." handoutUrl="https://docs.google.com/document/d/16heUGv_zg8PYiTwYIpSHeHpT005CKkr5qQaZ17Q7Z7g/edit?usp=sharing" handoutTitle="Transferable Skills - Complete Guide" onComplete={() => completeModule(3)} data-id="element-598" />
  }, {
    id: 'video-2',
    title: 'Training that actually works',
    type: 'video',
    completed: false,
    locked: true,
    duration: '22 min',
    content: <VideoContent title="Training that actually works" videoUrl="https://drive.google.com/file/d/1IttxAcenvQMFyKVTgpmlC0lO6FEfMdrK/view?usp=drive_link" onComplete={() => completeModule(4)} data-id="element-599" />
  }, {
    id: 'podcast-1',
    title: 'Podcast: Blueprint for Landing a Corporate Job',
    type: 'podcast',
    completed: false,
    locked: true,
    duration: '35 min',
    content: <PodcastContent title="Podcast: Blueprint for Landing a Corporate Job" audioUrl="https://mqqcxbzfpiqyieokdxta.supabase.co/storage/v1/object/public/GoABitFurther%20Academy/Teacher_to_Trainer__The_$85K_Blueprint_for_Landing_a_Corporate_.mp4" onComplete={() => completeModule(5)} data-id="element-600" />
  }, {
    id: 'text-2',
    title: 'Designing Effective Corporate Training Programs',
    type: 'text',
    completed: false,
    locked: true,
    duration: '20 min',
    content: <TextContent title="Designing Effective Corporate Training Programs" text="Corporate training programs differ from traditional educational curricula in several key ways. This module explores how to design training that addresses business needs, aligns with organizational goals, and delivers measurable results. You'll learn frameworks for needs analysis, program design, and evaluation methods that demonstrate ROI..." handoutUrl="https://docs.google.com/document/d/1-eveeixHpvsOBTwW7rInf_Ruo-wTqOSDL_6AI1qfNT8/edit?usp=sharing" handoutTitle="Effective Training Design - Complete Guide" onComplete={() => completeModule(6)} data-id="element-601" />
  }, {
    id: 'video-3',
    title: 'Technology in Corporate Training',
    type: 'video',
    completed: false,
    locked: true,
    duration: '25 min',
    content: <VideoContent title="Technology in Corporate Training" videoUrl="https://drive.google.com/file/d/1IttxAcenvQMFyKVTgpmlC0lO6FEfMdrK/view?usp=drive_link" onComplete={() => completeModule(7)} data-id="element-602" />
  }, {
    id: 'text-3',
    title: 'Building Your Corporate Trainer Brand',
    type: 'text',
    completed: false,
    locked: true,
    duration: '15 min',
    content: <TextContent title="Building Your Corporate Trainer Brand" text="Establishing yourself as a corporate trainer requires strategic personal branding. In this module, we'll explore how to position yourself in the market, build a compelling portfolio, craft an effective resume, and leverage professional networking to secure opportunities in corporate training..." handoutUrl="https://docs.google.com/document/d/1bulC1btnefSNpJK4gaHlDM1IKgf1he9AIM-ihLc5-yw/edit?usp=sharing" handoutTitle="Personal Branding Guide" onComplete={() => completeModule(8)} data-id="element-603" />
  }, {
    id: 'podcast-2',
    title: 'Industry Insight: Learning and Development Blueprint',
    type: 'podcast',
    completed: false,
    locked: true,
    duration: '30 min',
    content: <PodcastContent title="Industry Insight: Learning and Development Blueprint" audioUrl="https://mqqcxbzfpiqyieokdxta.supabase.co/storage/v1/object/public/Audio%20Course%20cont/Teacher_to_Trainer__The_L&D_Blueprint_for_Translating_Classroom.mp3" onComplete={() => completeModule(9)} data-id="element-604" />
  }, {
    id: 'mid-assessment',
    title: 'Mid-Course Assessment',
    type: 'assessment',
    completed: false,
    locked: true,
    duration: '20 min',
    content: <MidAssessmentContent onComplete={() => completeModule(10)} data-id="element-605" />
  }, {
    id: 'text-4',
    title: 'Career Paths and Growth Opportunities',
    type: 'text',
    completed: false,
    locked: true,
    duration: '15 min',
    content: <TextContent title="Career Paths and Growth Opportunities" text="The field of corporate training offers diverse career paths and growth opportunities. This final module explores various specializations within corporate training, advancement paths, salary expectations, and emerging trends that will shape the future of corporate learning and development..." handoutUrl="https://docs.google.com/document/d/1fB5gYk8QL4mYpb3QszDruJLbwOdwkzuN1eSXcyqngyA/edit?usp=sharing" handoutTitle="Career Paths Guide" onComplete={() => completeModule(11)} data-id="element-606" />
  }, {
    id: 'post-assessment',
    title: 'Final Assessment',
    type: 'assessment',
    completed: false,
    locked: true,
    duration: '30 min',
    content: <PostAssessmentContent onComplete={() => handlePostAssessmentComplete()} data-id="element-607" />
  }]);
  // Calculate progress percentage
  const completedModules = modules.filter(module => module.completed).length;
  const totalModules = modules.length;
  const progressPercentage = totalModules > 0 ? Math.round(completedModules / totalModules * 100) : 0;
  // Function to mark a module as completed and unlock the next one
  const completeModule = (index: number) => {
    console.log(`Completing module at index: ${index}`);
    if (index === 0) {
      setPreAssessmentCompleted(true);
    }
    setModules(prevModules => {
      const updatedModules = [...prevModules];
      updatedModules[index].completed = true;
      // Unlock the next module if it exists
      if (index < updatedModules.length - 1) {
        updatedModules[index + 1].locked = false;
        console.log(`Unlocking next module: ${updatedModules[index + 1].title}`);
      }
      return updatedModules;
    });
    // Move to the next module
    if (index < modules.length - 1) {
      console.log(`Moving to next module index: ${index + 1}`);
      setCurrentModuleIndex(index + 1);
    }
    // Save progress to Supabase
    if (courseId && modules[index] && user) {
      const completedModuleIds = modules.filter((module, i) => i <= index && module.completed).map(module => module.id);
      console.log(`Saving progress to Supabase, completed modules:`, completedModuleIds);
      // Save the traditional progress data
      saveProgress(courseId, modules[index].id, completedModuleIds).then(result => {
        if (result.error) {
          console.error('Failed to save progress:', result.error);
        } else {
          console.log('Progress saved successfully');
        }
      });
      // Also update the current_step using the new simplified approach
      updateCurrentStep(user.id, modules[index].id).then(result => {
        if (result.error) {
          console.error('Failed to update current step:', result.error);
        } else {
          console.log('Current step updated successfully');
        }
      });
      // Save using the simplified approach as well
      saveSimplifiedProgress(modules[index].id).then(() => {
        console.log('Simplified progress saved successfully');
      }).catch(err => {
        console.error('Error saving simplified progress:', err);
      });
    }
  };
  // Check if user is authenticated and load progress
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // If we have a courseId, fetch user progress
    if (courseId && user) {
      console.log('Loading progress for course:', courseId);
      // First try to load progress using the simplified method
      loadProgress().then(currentStep => {
        if (currentStep) {
          console.log(`Found current step from simplified progress: ${currentStep}`);
          // Find the module index for the current step
          const moduleIndex = modules.findIndex(m => m.id === currentStep);
          if (moduleIndex !== -1) {
            console.log(`Setting current module index to: ${moduleIndex}`);
            setCurrentModuleIndex(moduleIndex);
          }
        } else {
          console.log('No simplified progress found, trying detailed progress');
        }
      }).catch(err => {
        console.error('Error loading simplified progress:', err);
      });
      // Then fetch the full progress data
      fetchUserProgress(courseId).then(progress => {
        if (progress && progress.last_module) {
          console.log('Found detailed progress data:', progress);
          // Find the index of the last accessed module
          const lastModuleIndex = modules.findIndex(m => m.id === progress.last_module);
          if (lastModuleIndex !== -1) {
            // Unlock modules up to the last accessed one
            setModules(prevModules => {
              const updatedModules = [...prevModules];
              // Mark completed modules as completed and unlock them
              if (progress.completed_modules && Array.isArray(progress.completed_modules)) {
                progress.completed_modules.forEach(completedId => {
                  const moduleIndex = updatedModules.findIndex(m => m.id === completedId);
                  if (moduleIndex !== -1) {
                    updatedModules[moduleIndex].completed = true;
                    updatedModules[moduleIndex].locked = false;
                  }
                });
              }
              // Unlock modules up to the current one
              for (let i = 0; i <= lastModuleIndex; i++) {
                updatedModules[i].locked = false;
              }
              return updatedModules;
            });
            // Only set current module if it wasn't already set by the simplified progress
            if (!progress.current_step) {
              console.log(`Setting current module index to last module: ${lastModuleIndex}`);
              setCurrentModuleIndex(lastModuleIndex);
            }
            // If first module was completed, mark pre-assessment as completed
            if (progress.completed_modules && progress.completed_modules.includes(modules[0].id)) {
              setPreAssessmentCompleted(true);
            }
          }
        }
      });
    }
  }, [isAuthenticated, navigate, courseId, user, modules]);
  // Function to handle post-assessment completion
  const handlePostAssessmentComplete = () => {
    completeModule(12);
    setShowCertificateNameForm(true);
    // Update the current step to show certificate form
    if (user) {
      updateCurrentStep(user.id, 'certificate-form');
    }
  };
  // Handle certificate name form submission
  const handleCertificateGeneration = (e: React.FormEvent) => {
    e.preventDefault();
    if (certificateName.trim() === '') {
      alert('Please enter your name for the certificate');
      return;
    }
    setShowCertificateNameForm(false);
    setCertificateGenerated(true);
    // Update the current step to show certificate
    if (user) {
      updateCurrentStep(user.id, 'certificate-generated');
    }
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-608">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-609">
        {/* Course Header */}
        <div className="mb-8" data-id="element-610">
          <button onClick={() => navigate('/courses')} className="flex items-center text-blue-600 hover:text-blue-800 mb-4" data-id="element-611">
            <ArrowLeft size={16} className="mr-1" data-id="element-612" />
            Back to all courses
          </button>
          <h1 className="text-3xl font-bold text-gray-800 mb-2" data-id="element-613">
            {courseTitle}
          </h1>
          <p className="text-xl text-gray-600 mb-6" data-id="element-614">{courseSubtitle}</p>
          {/* Progress Bar */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-615">
            <div className="flex justify-between items-center mb-2" data-id="element-616">
              <h3 className="text-lg font-medium text-gray-800" data-id="element-617">
                Your Progress
              </h3>
              <span className="text-sm font-medium text-blue-600" data-id="element-618">
                {progressPercentage}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-619">
              <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" style={{
              width: `${progressPercentage}%`
            }} data-id="element-620"></div>
            </div>
            {/* Current module indicator */}
            <p className="text-sm text-gray-600" data-id="element-621">
              Currently on:{' '}
              <span className="font-medium text-gray-800" data-id="element-622">
                {modules[currentModuleIndex].title}
              </span>
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-623">
          {/* Course Modules Sidebar */}
          <div className="lg:col-span-1 order-2 lg:order-1" data-id="element-624">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6 lg:mb-0 lg:sticky lg:top-8 max-h-[calc(100vh-120px)] overflow-y-auto" data-id="element-625">
              <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-626">
                Course Modules
              </h3>
              <div className="space-y-1" data-id="element-627">
                {modules.map((module, index) => <button key={module.id} onClick={() => {
                if (!module.locked) {
                  setCurrentModuleIndex(index);
                }
              }} disabled={module.locked} className={`w-full flex items-center justify-between p-3 rounded-md text-sm ${currentModuleIndex === index ? 'bg-blue-50 text-blue-700 font-medium' : module.completed ? 'text-green-700 bg-green-50' : module.locked ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50'} transition-colors`} data-id="element-628">
                    <div className="flex items-center" data-id="element-629">
                      {module.completed ? <CheckCircle size={16} className="mr-2 text-green-500 flex-shrink-0" data-id="element-630" /> : module.locked ? <Lock size={16} className="mr-2 text-gray-400 flex-shrink-0" data-id="element-631" /> : getModuleIcon(module.type, 16, 'mr-2 text-blue-500 flex-shrink-0')}
                      <span className="truncate" data-id="element-632">{module.title}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-500 ml-2 flex-shrink-0" data-id="element-633">
                      <Clock size={12} className="mr-1" data-id="element-634" />
                      {module.duration}
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
          {/* Main Content Area */}
          <div className="lg:col-span-2 order-1 lg:order-2" data-id="element-635">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6" data-id="element-636">
              {/* Certificate Name Form */}
              {showCertificateNameForm && !certificateGenerated ? <div className="text-center py-8" data-id="element-637">
                  <Award size={64} className="mx-auto text-blue-600 mb-4" data-id="element-638" />
                  <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-639">
                    Congratulations!
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-640">
                    You've successfully completed the course. Please enter your
                    full name as you would like it to appear on your
                    certificate.
                  </p>
                  <form onSubmit={handleCertificateGeneration} className="max-w-md mx-auto" data-id="element-641">
                    <div className="mb-4" data-id="element-642">
                      <label htmlFor="certificateName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-643">
                        Full Name
                      </label>
                      <input type="text" id="certificateName" value={certificateName} onChange={e => setCertificateName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter your full name" required data-id="element-644" />
                    </div>
                    <button type="submit" className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors" data-id="element-645">
                      Generate My Certificate
                    </button>
                  </form>
                </div> : certificateGenerated ? <Certificate name={certificateName} courseTitle={courseTitle} data-id="element-646" /> :
            // Current Module Content
            <div data-id="element-647">
                  <div className="flex items-center justify-between mb-6" data-id="element-648">
                    <h2 className="text-xl font-semibold text-gray-800" data-id="element-649">
                      {modules[currentModuleIndex].title}
                    </h2>
                    <div className="flex items-center text-sm text-gray-500" data-id="element-650">
                      <Clock size={16} className="mr-1" data-id="element-651" />
                      {modules[currentModuleIndex].duration}
                    </div>
                  </div>
                  {/* Module content */}
                  {modules[currentModuleIndex].content}
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
// Helper function to get the appropriate icon for each module type
const getModuleIcon = (type: ContentType, size: number = 20, className: string = '') => {
  switch (type) {
    case 'text':
      return <FileText size={size} className={className} data-id="element-652" />;
    case 'video':
      return <Video size={size} className={className} data-id="element-653" />;
    case 'podcast':
      return <Headphones size={size} className={className} data-id="element-654" />;
    case 'assessment':
      return <BookOpen size={size} className={className} data-id="element-655" />;
    default:
      return <FileText size={size} className={className} data-id="element-656" />;
  }
};
// Content Components
const TextContent: React.FC<{
  title: string;
  text: string;
  handoutUrl?: string;
  handoutTitle?: string;
  onComplete: () => void;
}> = ({
  title,
  text,
  handoutUrl,
  handoutTitle,
  onComplete
}) => {
  const [showDocument, setShowDocument] = useState(false);
  return <div className="prose max-w-none" data-id="element-657">
      {!showDocument ? <>
          <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-658">{title}</h3>
          <div className="text-gray-700 mb-8" data-id="element-659">
            <p className="mb-4" data-id="element-660">{text}</p>
            <p data-id="element-661">
              This course provides practical guidance and real-world examples to
              help you apply your educational background in new professional
              contexts. You'll learn how to leverage your existing skills while
              developing new competencies needed for success.
            </p>
          </div>
          {handoutUrl && handoutTitle && <div className="mt-6 mb-6" data-id="element-662">
              <button onClick={() => setShowDocument(true)} className="flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-663">
                <ExternalLink size={16} className="mr-2" data-id="element-664" />
                View full document: {handoutTitle}
              </button>
              <DownloadHandout title={handoutTitle} fileUrl={handoutUrl} fileType="pdf" data-id="element-665" />
            </div>}
          <button onClick={onComplete} className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors mt-6" data-id="element-666">
            Mark as Complete
            <ChevronRight size={16} className="ml-1" data-id="element-667" />
          </button>
        </> : <DocumentViewer title={handoutTitle || title} fileUrl={handoutUrl || ''} onComplete={onComplete} data-id="element-668" />}
    </div>;
};
const VideoContent: React.FC<{
  title: string;
  videoUrl: string;
  onComplete: () => void;
}> = ({
  title,
  videoUrl,
  onComplete
}) => {
  // Import the getEmbedUrl function from the mediaUtils file
  const {
    getEmbedUrl
  } = require('../utils/mediaUtils');
  return <div data-id="element-669">
      <div className="aspect-w-16 aspect-h-9 bg-gray-100 rounded-lg mb-6 overflow-hidden" data-id="element-670">
        <iframe src={getEmbedUrl(videoUrl)} className="w-full h-full rounded-lg" title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen frameBorder="0" data-id="element-671"></iframe>
      </div>
      <div className="prose max-w-none mb-8" data-id="element-672">
        <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-673">{title}</h3>
        <p className="text-gray-700" data-id="element-674">
          In this video, you'll learn about the key principles and practices of
          effective corporate training. The video covers instructional design
          approaches, engagement strategies, and assessment techniques that are
          specifically tailored for adult learners in corporate environments.
        </p>
      </div>
      <button onClick={onComplete} className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-675">
        Mark as Watched
        <ChevronRight size={16} className="ml-1" data-id="element-676" />
      </button>
    </div>;
};
const PodcastContent: React.FC<{
  title: string;
  audioUrl: string;
  onComplete: () => void;
}> = ({
  title,
  audioUrl,
  onComplete
}) => {
  // Import the getAudioStreamUrl function from the mediaUtils file
  const {
    getAudioStreamUrl
  } = require('../utils/mediaUtils');
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const audio = e.currentTarget;
    setCurrentTime(audio.currentTime);
    setProgress(audio.currentTime / audio.duration * 100);
  };
  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    setDuration(e.currentTarget.duration);
  };
  const togglePlayPause = () => {
    const audioElement = document.getElementById('podcast-audio') as HTMLAudioElement;
    if (audioElement) {
      if (isPlaying) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
    }
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = document.getElementById('podcast-audio') as HTMLAudioElement;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const clickedValue = x / rect.width * audio.duration;
    audio.currentTime = clickedValue;
  };
  return <div data-id="element-677">
      <div className="bg-gray-100 rounded-lg p-6 mb-6" data-id="element-678">
        <div className="flex items-center justify-between mb-4" data-id="element-679">
          <div className="flex items-center" data-id="element-680">
            <div className="bg-blue-600 rounded-full p-3" data-id="element-681">
              <Headphones size={24} className="text-white" data-id="element-682" />
            </div>
            <div className="ml-4" data-id="element-683">
              <h3 className="font-medium text-gray-800" data-id="element-684">{title}</h3>
              <p className="text-sm text-gray-500" data-id="element-685">Episode 1 • 35 min</p>
            </div>
          </div>
          <button onClick={togglePlayPause} className="bg-blue-600 text-white rounded-full p-3 hover:bg-blue-700 transition-colors" data-id="element-686">
            {isPlaying ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-687">
                <rect x="6" y="4" width="4" height="16" data-id="element-688"></rect>
                <rect x="14" y="4" width="4" height="16" data-id="element-689"></rect>
              </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-690">
                <polygon points="5 3 19 12 5 21 5 3" data-id="element-691"></polygon>
              </svg>}
          </button>
        </div>
        {/* Hidden audio element that actually plays the content */}
        <audio id="podcast-audio" src={getAudioStreamUrl(audioUrl)} onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} className="hidden" data-id="element-692" />
        {/* Custom audio controls */}
        <div className="mb-2" data-id="element-693">
          <div className="w-full bg-gray-300 rounded-full h-1.5 cursor-pointer" onClick={handleSeek} data-id="element-694">
            <div className="bg-blue-600 h-1.5 rounded-full" style={{
            width: `${progress}%`
          }} data-id="element-695"></div>
          </div>
        </div>
        <div className="flex justify-between text-sm text-gray-500" data-id="element-696">
          <span data-id="element-697">{formatTime(currentTime)}</span>
          <span data-id="element-698">{formatTime(duration)}</span>
        </div>
      </div>
      <div className="prose max-w-none mb-8" data-id="element-699">
        <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-700">
          About This Episode
        </h3>
        <p className="text-gray-700 mb-4" data-id="element-701">
          In this podcast, we interview former educators who have successfully
          transitioned to corporate training roles. They share their journey,
          challenges, and insights to help you navigate your own career
          transition.
        </p>
        <p className="text-gray-700" data-id="element-702">
          Listen as they discuss how they leveraged their teaching experience,
          adapted to corporate culture, and built successful careers in learning
          and development.
        </p>
      </div>
      <button onClick={onComplete} className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-703">
        Mark as Listened
        <ChevronRight size={16} className="ml-1" data-id="element-704" />
      </button>
    </div>;
};
const PreAssessmentContent: React.FC<{
  onComplete: () => void;
}> = ({
  onComplete
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>(Array(5).fill(-1));
  const [showResults, setShowResults] = useState(false);
  const [submittingData, setSubmittingData] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: ''
  });
  const {
    user
  } = useAuth();
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const questions = [{
    question: 'What is your primary motivation for transitioning from education to corporate training?',
    options: ['Higher salary potential', 'More diverse career opportunities', 'Interest in adult learning and development', 'Desire to apply teaching skills in business contexts']
  }, {
    question: 'How would you rate your current knowledge of corporate training methodologies?',
    options: ['No knowledge', 'Basic understanding', 'Moderate knowledge', 'Advanced understanding']
  }, {
    question: 'Which aspect of corporate training interests you most?',
    options: ['Instructional design', 'Facilitation and delivery', 'Learning technology', 'Program management and evaluation']
  }, {
    question: 'What do you consider your strongest transferable skill from education?',
    options: ['Curriculum development', 'Presentation and public speaking', 'Assessment and evaluation', 'Learner engagement strategies']
  }, {
    question: 'What is your biggest concern about transitioning to corporate training?',
    options: ['Lack of corporate experience', 'Understanding business contexts', 'Technical knowledge requirements', 'Building a professional network']
  }];
  const handleAnswer = (questionIndex: number, optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
    // Save individual answer to Supabase
    if (courseId) {
      saveAssessmentResponse('pre', `q${questionIndex + 1}`, optionIndex).catch(err => console.error('Error saving assessment response:', err));
    }
  };
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  const handleUserInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmitAssessment = async () => {
    setSubmittingData(true);
    try {
      // Prepare data for Google Sheets
      const submissionData = {
        timestamp: new Date().toISOString(),
        name: userInfo.name || user?.name || '',
        email: userInfo.email || user?.email || '',
        q1: questions[0].options[answers[0]],
        q2: questions[1].options[answers[1]],
        q3: questions[2].options[answers[2]],
        q4: questions[3].options[answers[3]],
        q5: questions[4].options[answers[4]]
      };
      // Log the data that would be submitted
      console.log('Submitting pre-assessment data:', submissionData);
      // Save final submission to Supabase
      if (courseId) {
        try {
          const result = await saveAssessmentResponse('pre', 'complete', JSON.stringify(answers));
          if (result.error) {
            console.error('Error saving final assessment:', result.error);
          }
        } catch (err) {
          console.error('Error during assessment save:', err);
        }
      }
      // In a real implementation, you would submit this data to Google Sheets
      // For now, we'll simulate a successful submission
      setTimeout(() => {
        setSubmittingData(false);
        onComplete();
      }, 1500);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setSubmittingData(false);
    }
  };
  if (showResults) {
    return <div className="text-center py-6" data-id="element-705">
        <CheckCircle size={64} className="mx-auto text-green-500 mb-4" data-id="element-706" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-707">
          Pre-Assessment Complete!
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-708">
          Thank you for completing the pre-assessment. This helps us understand
          your background and tailor the course to your needs.
        </p>
        <div className="bg-gray-50 p-6 rounded-lg mb-6 text-left" data-id="element-709">
          <h3 className="text-lg font-medium text-gray-800 mb-4" data-id="element-710">
            Please confirm your information
          </h3>
          <div className="space-y-4" data-id="element-711">
            <div data-id="element-712">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-713">
                Full Name
              </label>
              <input type="text" id="name" name="name" value={userInfo.name || user?.name || ''} onChange={handleUserInfoChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Your full name" required data-id="element-714" />
            </div>
            <div data-id="element-715">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-716">
                Email Address
              </label>
              <input type="email" id="email" name="email" value={userInfo.email || user?.email || ''} onChange={handleUserInfoChange} className="w-full px-3 py-2 border border-gray-300 rounded-md" placeholder="Your email address" required data-id="element-717" />
            </div>
          </div>
        </div>
        <button onClick={handleSubmitAssessment} disabled={submittingData} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400" data-id="element-718">
          {submittingData ? 'Submitting...' : 'Start the Course'}
        </button>
      </div>;
  }
  return <div data-id="element-719">
      <div className="mb-6" data-id="element-720">
        <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-721">
          Pre-Assessment Quiz
        </h3>
        <p className="text-gray-600 mb-4" data-id="element-722">
          This brief assessment helps us understand your background and goals.
          There are no right or wrong answers - this is about personalizing your
          learning experience.
        </p>
      </div>
      <div className="mb-8" data-id="element-723">
        <div className="flex justify-between items-center mb-4" data-id="element-724">
          <span className="text-sm font-medium text-gray-700" data-id="element-725">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex space-x-1" data-id="element-726">
            {questions.map((_, index) => <div key={index} className={`h-2 w-10 rounded-full ${index === currentQuestion ? 'bg-blue-600' : answers[index] >= 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-727"></div>)}
          </div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg" data-id="element-728">
          <h4 className="text-lg font-medium text-gray-800 mb-4" data-id="element-729">
            {questions[currentQuestion].question}
          </h4>
          <div className="space-y-3" data-id="element-730">
            {questions[currentQuestion].options.map((option, index) => <div key={index} onClick={() => handleAnswer(currentQuestion, index)} className={`p-4 rounded-lg border cursor-pointer transition-colors ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`} data-id="element-731">
                <div className="flex items-center" data-id="element-732">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-500' : 'border-gray-400'}`} data-id="element-733">
                    {answers[currentQuestion] === index && <div className="w-2 h-2 rounded-full bg-white" data-id="element-734"></div>}
                  </div>
                  <span className="text-gray-700" data-id="element-735">{option}</span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <div className="flex justify-between" data-id="element-736">
        <button onClick={handlePrevious} disabled={currentQuestion === 0} className={`px-4 py-2 rounded-md font-medium ${currentQuestion === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-737">
          Previous
        </button>
        <button onClick={handleNext} disabled={answers[currentQuestion] === -1} className={`px-4 py-2 rounded-md font-medium ${answers[currentQuestion] === -1 ? 'bg-blue-300 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-738">
          {currentQuestion < questions.length - 1 ? 'Next' : 'Finish'}
        </button>
      </div>
    </div>;
};
const MidAssessmentContent: React.FC<{
  onComplete: () => void;
}> = ({
  onComplete
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>(Array(3).fill(-1));
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [submittingData, setSubmittingData] = useState(false);
  const {
    user
  } = useAuth();
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const questions = [{
    question: 'Which of the following is NOT typically a responsibility of a corporate trainer?',
    options: ['Conducting needs assessments', 'Developing training materials', 'Grading academic assignments', 'Evaluating training effectiveness'],
    correctAnswer: 2
  }, {
    question: 'What is the ADDIE model used for in corporate training?',
    options: ['Employee performance evaluation', 'Instructional design process', 'Corporate hierarchy structure', 'Budget allocation for training'],
    correctAnswer: 1
  }, {
    question: 'Which skill from teaching transfers most directly to facilitating corporate workshops?',
    options: ['Classroom management', 'Curriculum development', 'Student grading', 'Parent communication'],
    correctAnswer: 0
  }];
  const handleAnswer = (questionIndex: number, optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
    // Save individual answer to Supabase
    const {
      courseId
    } = useParams<{
      courseId: string;
    }>();
    if (courseId) {
      saveAssessmentResponse('mid', `q${questionIndex + 1}`, optionIndex, optionIndex === questions[questionIndex].correctAnswer).catch(err => console.error('Error saving assessment response:', err));
    }
  };
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate score
      const correctCount = answers.reduce((count, answer, index) => {
        return count + (answer === questions[index].correctAnswer ? 1 : 0);
      }, 0);
      const percentage = Math.round(correctCount / questions.length * 100);
      setScore(percentage);
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  const handleSubmitAssessment = async () => {
    setSubmittingData(true);
    try {
      // Prepare data for submission
      const submissionData = {
        timestamp: new Date().toISOString(),
        name: user?.name || '',
        email: user?.email || '',
        score: score,
        q1_answer: answers[0],
        q1_correct: answers[0] === questions[0].correctAnswer,
        q2_answer: answers[1],
        q2_correct: answers[1] === questions[1].correctAnswer,
        q3_answer: answers[2],
        q3_correct: answers[2] === questions[2].correctAnswer
      };
      // Log data for debugging
      console.log('Submitting mid-course assessment data:', submissionData);
      // Save final submission to Supabase
      if (courseId) {
        try {
          const result = await saveAssessmentResponse('mid', 'complete', JSON.stringify(answers));
          if (result.error) {
            console.error('Error saving final assessment:', result.error);
          }
        } catch (err) {
          console.error('Error during assessment save:', err);
        }
      }
      // Complete this module
      onComplete();
      // Find the mid-assessment index and navigate to the next module
      const midAssessmentIndex = modules.findIndex(module => module.id === 'mid-assessment');
      if (midAssessmentIndex !== -1 && midAssessmentIndex < modules.length - 1) {
        // Set the next module as active
        setCurrentModuleIndex(midAssessmentIndex + 1);
      }
      setSubmittingData(false);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setSubmittingData(false);
      // Still try to complete if there was an error
      onComplete();
    }
  };
  if (showResults) {
    return <div className="text-center py-6" data-id="element-739">
        <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4" data-id="element-740">
          <span className="text-2xl font-bold text-blue-600" data-id="element-741">{score}%</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-742">
          Mid-Course Assessment Complete
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-743">
          You've completed the mid-course assessment with a score of {score}%.
          {score >= 70 ? " Great job! You're making excellent progress." : ' Keep going! Review the previous modules to strengthen your understanding.'}
        </p>
        <button onClick={handleSubmitAssessment} disabled={submittingData} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-400" data-id="element-744">
          {submittingData ? 'Submitting...' : 'Continue to Next Module'}
        </button>
      </div>;
  }
  return <div data-id="element-745">
      <div className="mb-6" data-id="element-746">
        <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-747">
          Mid-Course Assessment
        </h3>
        <p className="text-gray-600 mb-4" data-id="element-748">
          Let's check your understanding of the key concepts covered so far.
          This assessment will help reinforce your learning and identify any
          areas that need review.
        </p>
      </div>
      <div className="mb-8" data-id="element-749">
        <div className="flex justify-between items-center mb-4" data-id="element-750">
          <span className="text-sm font-medium text-gray-700" data-id="element-751">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex space-x-1" data-id="element-752">
            {questions.map((_, index) => <div key={index} className={`h-2 w-10 rounded-full ${index === currentQuestion ? 'bg-blue-600' : answers[index] >= 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-753"></div>)}
          </div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg" data-id="element-754">
          <h4 className="text-lg font-medium text-gray-800 mb-4" data-id="element-755">
            {questions[currentQuestion].question}
          </h4>
          <div className="space-y-3" data-id="element-756">
            {questions[currentQuestion].options.map((option, index) => <div key={index} onClick={() => handleAnswer(currentQuestion, index)} className={`p-4 rounded-lg border cursor-pointer transition-colors ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`} data-id="element-757">
                <div className="flex items-center" data-id="element-758">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-500' : 'border-gray-400'}`} data-id="element-759">
                    {answers[currentQuestion] === index && <div className="w-2 h-2 rounded-full bg-white" data-id="element-760"></div>}
                  </div>
                  <span className="text-gray-700" data-id="element-761">{option}</span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <div className="flex justify-between" data-id="element-762">
        <button onClick={handlePrevious} disabled={currentQuestion === 0} className={`px-4 py-2 rounded-md font-medium ${currentQuestion === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-763">
          Previous
        </button>
        <button onClick={handleNext} disabled={answers[currentQuestion] === -1} className={`px-4 py-2 rounded-md font-medium ${answers[currentQuestion] === -1 ? 'bg-blue-300 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-764">
          {currentQuestion < questions.length - 1 ? 'Next' : 'Finish'}
        </button>
      </div>
    </div>;
};
const PostAssessmentContent: React.FC<{
  onComplete: () => void;
}> = ({
  onComplete
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>(Array(5).fill(-1));
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [submittingData, setSubmittingData] = useState(false);
  const [reviewRequired, setReviewRequired] = useState(false);
  const {
    user
  } = useAuth();
  const {
    courseId
  } = useParams<{
    courseId: string;
  }>();
  const questions = [{
    question: 'Which of the following best describes the role of a needs assessment in corporate training?',
    options: ['Evaluating trainer performance', 'Identifying skill gaps and training requirements', 'Determining employee salaries', 'Selecting training venues'],
    correctAnswer: 1
  }, {
    question: 'What is ROI in the context of corporate training?',
    options: ['Rate of Instruction', 'Return on Investment', 'Review of Implementation', 'Record of Interaction'],
    correctAnswer: 1
  }, {
    question: 'Which adult learning principle emphasizes the importance of practical application in corporate training?',
    options: ['Self-direction', 'Prior experience', 'Relevance and application to real-life', 'Intrinsic motivation'],
    correctAnswer: 2
  }, {
    question: 'Which of the following is NOT typically included in a corporate training portfolio?',
    options: ['Sample training materials', 'Evaluation results', 'Academic transcripts', 'Client testimonials'],
    correctAnswer: 2
  }, {
    question: 'What is microlearning in corporate training?',
    options: ['Training only small groups of employees', 'Delivering content in small, focused segments', 'Training on microscopic subjects', 'Learning with minimal resources'],
    correctAnswer: 1
  }];
  const handleAnswer = (questionIndex: number, optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = optionIndex;
    setAnswers(newAnswers);
    // Save individual answer to Supabase
    if (courseId) {
      saveAssessmentResponse('post', `q${questionIndex + 1}`, optionIndex, optionIndex === questions[questionIndex].correctAnswer).catch(err => console.error('Error saving assessment response:', err));
    }
  };
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate score
      const correctCount = answers.reduce((count, answer, index) => {
        return count + (answer === questions[index].correctAnswer ? 1 : 0);
      }, 0);
      const percentage = Math.round(correctCount / questions.length * 100);
      setScore(percentage);
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  const handleSubmitAssessment = async () => {
    setSubmittingData(true);
    try {
      // Check if score is below 70%
      if (score < 70) {
        setReviewRequired(true);
        setSubmittingData(false);
        return;
      }
      // Prepare data for Google Sheets
      const submissionData = {
        timestamp: new Date().toISOString(),
        name: user?.name || '',
        email: user?.email || '',
        score: score,
        q1_answer: answers[0],
        q1_correct: answers[0] === questions[0].correctAnswer,
        q2_answer: answers[1],
        q2_correct: answers[1] === questions[1].correctAnswer,
        q3_answer: answers[2],
        q3_correct: answers[2] === questions[2].correctAnswer,
        q4_answer: answers[3],
        q4_correct: answers[3] === questions[3].correctAnswer,
        q5_answer: answers[4],
        q5_correct: answers[4] === questions[4].correctAnswer
      };
      // Log the data that would be submitted
      console.log('Submitting post-assessment data:', submissionData);
      // Save final submission to Supabase
      if (courseId) {
        try {
          const result = await saveAssessmentResponse('post', 'complete', JSON.stringify(answers), score >= 70);
          if (result.error) {
            console.error('Error saving final assessment:', result.error);
          }
        } catch (err) {
          console.error('Error during assessment save:', err);
        }
      }
      setSubmittingData(false);
      // Complete the assessment and move to certificate name form
      onComplete();
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setSubmittingData(false);
    }
  };
  if (showResults) {
    return <div className="text-center py-6" data-id="element-765">
        <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4" data-id="element-766">
          <span className="text-2xl font-bold text-blue-600" data-id="element-767">{score}%</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-768">
          Final Assessment Complete
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-769">
          {score >= 70 ? "Congratulations! You've completed the final assessment with a score of " + score + "%. You've demonstrated a strong understanding of corporate training concepts and practices." : "You've completed the final assessment with a score of " + score + '%. You need to score at least 70% to receive your certificate. Please review the course materials and try again.'}
        </p>
        {reviewRequired ? <div data-id="element-770">
            <p className="text-red-600 mb-4" data-id="element-771">
              Your score is below the required 70% passing threshold. Please
              review the course materials before attempting again.
            </p>
            <button onClick={() => {
          setReviewRequired(false);
          setShowResults(false);
          setCurrentQuestion(0);
          setAnswers(Array(5).fill(-1));
        }} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-772">
              Retake Assessment
            </button>
          </div> : <button onClick={handleSubmitAssessment} disabled={submittingData || score < 70} className={`px-6 py-2 rounded-md transition-colors ${score < 70 ? 'bg-gray-400 text-white cursor-not-allowed' : submittingData ? 'bg-blue-400 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-773">
            {submittingData ? 'Submitting...' : score < 70 ? 'Score Too Low' : 'Get Your Certificate'}
          </button>}
      </div>;
  }
  return <div data-id="element-774">
      <div className="mb-6" data-id="element-775">
        <h3 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-776">
          Final Assessment
        </h3>
        <p className="text-gray-600 mb-4" data-id="element-777">
          This assessment evaluates your understanding of the key concepts
          covered in the course. Successfully completing this assessment will
          allow you to receive your course certificate.
        </p>
      </div>
      <div className="mb-8" data-id="element-778">
        <div className="flex justify-between items-center mb-4" data-id="element-779">
          <span className="text-sm font-medium text-gray-700" data-id="element-780">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <div className="flex space-x-1" data-id="element-781">
            {questions.map((_, index) => <div key={index} className={`h-2 w-8 rounded-full ${index === currentQuestion ? 'bg-blue-600' : answers[index] >= 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-782"></div>)}
          </div>
        </div>
        <div className="bg-gray-50 p-6 rounded-lg" data-id="element-783">
          <h4 className="text-lg font-medium text-gray-800 mb-4" data-id="element-784">
            {questions[currentQuestion].question}
          </h4>
          <div className="space-y-3" data-id="element-785">
            {questions[currentQuestion].options.map((option, index) => <div key={index} onClick={() => handleAnswer(currentQuestion, index)} className={`p-4 rounded-lg border cursor-pointer transition-colors ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`} data-id="element-786">
                <div className="flex items-center" data-id="element-787">
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-500' : 'border-gray-400'}`} data-id="element-788">
                    {answers[currentQuestion] === index && <div className="w-2 h-2 rounded-full bg-white" data-id="element-789"></div>}
                  </div>
                  <span className="text-gray-700" data-id="element-790">{option}</span>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      <div className="flex justify-between" data-id="element-791">
        <button onClick={handlePrevious} disabled={currentQuestion === 0} className={`px-4 py-2 rounded-md font-medium ${currentQuestion === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-792">
          Previous
        </button>
        <button onClick={handleNext} disabled={answers[currentQuestion] === -1} className={`px-4 py-2 rounded-md font-medium ${answers[currentQuestion] === -1 ? 'bg-blue-300 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-793">
          {currentQuestion < questions.length - 1 ? 'Next' : 'Finish'}
        </button>
      </div>
    </div>;
};
// Updated Certificate component to use PDF-lib for certificate generation
const Certificate: React.FC<{
  name: string;
  courseTitle: string;
  certificateUrl?: string;
}> = ({
  name,
  courseTitle
}) => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const certificateRef = useRef<HTMLDivElement>(null);
  // Function to generate and download PDF certificate using PDF-lib
  const handleDownloadPDFCertificate = async () => {
    try {
      // Load the PDF-lib library
      const {
        PDFDocument,
        rgb
      } = await import('https://cdn.skypack.dev/pdf-lib');
      // Certificate template URL
      const TEMPLATE_URL = 'https://mqqcxbzfpiqyieokdxta.supabase.co/storage/v1/object/public/Completion%20Certificate/certificate%20template.pdf';
      // Fetch the template PDF
      const existingPdfBytes = await fetch(TEMPLATE_URL).then(r => {
        if (!r.ok) throw new Error('Could not fetch template: ' + r.status);
        return r.arrayBuffer();
      });
      // Load the PDF document
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      // Position for the name (adjust these coordinates as needed)
      const nameX = 320;
      const nameY = 320;
      const fontSize = 28;
      // Add the name to the certificate
      firstPage.drawText(name, {
        x: nameX,
        y: nameY,
        size: fontSize,
        color: rgb(0, 0, 0)
      });
      // Add course title (adjust coordinates as needed)
      firstPage.drawText(courseTitle, {
        x: 320,
        y: 280,
        size: 18,
        color: rgb(0, 0, 0)
      });
      // Add completion date (adjust coordinates as needed)
      firstPage.drawText(currentDate, {
        x: 320,
        y: 240,
        size: 16,
        color: rgb(0, 0, 0)
      });
      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      // Download the PDF
      const blob = new Blob([pdfBytes], {
        type: 'application/pdf'
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `certificate-${name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Certificate generation error:', error);
      alert('Failed to generate certificate. Please try again.');
    }
  };
  // Traditional certificate preview (remains for visual display)
  return <div className="text-center py-6" data-id="element-794">
      <div ref={certificateRef} className="border-8 border-blue-200 rounded-lg p-8 mb-6 bg-white max-w-2xl mx-auto" data-id="element-795">
        <div className="border-4 border-blue-100 rounded p-8" data-id="element-796">
          <div className="text-center" data-id="element-797">
            <div className="text-blue-600 font-bold text-xl mb-1" data-id="element-798">
              GoABitFurther Africa
            </div>
            <div className="text-gray-600 font-medium mb-8" data-id="element-799">
              Certificate of Completion
            </div>
            <div className="mb-6" data-id="element-800">
              <img src="https://cdn-icons-png.flaticon.com/512/6295/6295417.png" alt="Certificate Badge" className="w-24 h-24 mx-auto" data-id="element-801" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-802">
              This certifies that
            </h1>
            <p className="text-2xl font-semibold text-blue-700 mb-8" data-id="element-803">{name}</p>
            <p className="text-gray-600 mb-2" data-id="element-804">
              has successfully completed the course
            </p>
            <p className="text-xl font-semibold text-gray-800 mb-8" data-id="element-805">
              "{courseTitle}"
            </p>
            <p className="text-gray-600 mb-8" data-id="element-806">on {currentDate}</p>
            <div className="flex justify-center items-center mb-4" data-id="element-807">
              <div className="w-32 border-t-2 border-gray-400 mr-4" data-id="element-808"></div>
              <img src="https://www.pngkey.com/png/full/147-1477347_signature-png-transparent-background-signature-transparent-background.png" alt="Signature" className="w-32 h-16 object-contain" data-id="element-809" />
              <div className="w-32 border-t-2 border-gray-400 ml-4" data-id="element-810"></div>
            </div>
            <p className="text-sm text-gray-500" data-id="element-811">
              Director, GoABitFurther Africa
            </p>
          </div>
        </div>
      </div>
      <p className="text-gray-600 mb-6" data-id="element-812">
        Your certificate has been prepared with your name. You can download it
        using the button below.
      </p>
      <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-813">
        <button onClick={handleDownloadCertificate} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center mx-auto" data-id="element-814">
          <Download className="mr-2" size={16} data-id="element-815" />
          Download Image Certificate
        </button>
        <button onClick={handleDownloadPDFCertificate} className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center mx-auto" data-id="element-816">
          <Download className="mr-2" size={16} data-id="element-817" />
          Download PDF Certificate
        </button>
      </div>
    </div>;
};
export default CourseDetail;